import { z } from "zod";
import { RichText } from "../RichText";
import { useRenderContext, type BlogPostEntry } from "../RenderContext";
import { globalSectionSchema, subHeadingLevel } from "../globalProps";
import { SectionShell } from "../SectionShell";

export const schema = globalSectionSchema.extend({
  heading: z.string().default("Latest Insights"),
  subheading: z.string().optional(),
  count: z.number().int().min(1).default(3),
  tagFilter: z.string().optional(),
  layout: z.enum(["grid", "featured"]).default("grid"),
  columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
  showDate: z.boolean().default(false),
  showAuthor: z.boolean().default(false),
  readMoreLabel: z.string().optional(),
  imageAspect: z.enum(["video", "4:3", "1:1"]).default("video"),
  featuredCount: z.number().int().min(1).default(1),
  listCount: z.number().int().min(1).default(4),
  pinnedPostIds: z.array(z.string()).nullable().default(null),
});

export type Props = z.infer<typeof schema>;
export const defaults: Props = schema.parse({});

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  author?: string;
  summary?: string;
  publishAt?: string;
  featuredImageUrl?: string;
}

function formatDateSlug(dateStr: string | null | undefined, slug: string): string {
  if (!dateStr) return slug;
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}-${slug}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/* ---------- Layout: Grid (default) ---------- */

const gridColClasses: Record<number, string> = {
  2: "grid-cols-1 @md:grid-cols-2",
  3: "grid-cols-1 @md:grid-cols-3",
  4: "grid-cols-1 @md:grid-cols-2 @lg:grid-cols-4",
};

const imageAspectClasses: Record<string, string> = {
  video: "aspect-video",
  "4:3": "aspect-[4/3]",
  "1:1": "aspect-square",
};

function GridLayout({ posts, blogBasePath, SubH, columns, showDate, showAuthor, readMoreLabel, imageAspect }: { posts: BlogPost[]; blogBasePath: string; SubH: "h2" | "h3" | "h4"; columns: number; showDate: boolean; showAuthor: boolean; readMoreLabel?: string; imageAspect: string }) {
  const aspectClass = imageAspectClasses[imageAspect] || imageAspectClasses.video;
  return (
    <div className={`grid ${gridColClasses[columns]} gap-8`}>
      {posts.map((post) => (
        <a
          key={post.id}
          href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
          className="bg-white text-text rounded-lg shadow-md overflow-hidden group"
        >
          {post.featuredImageUrl && (
            <div className={`${aspectClass} overflow-hidden`}>
              <img
                src={post.featuredImageUrl}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                loading="lazy"
              />
            </div>
          )}
          <div className="p-5">
            <SubH className="font-semibold text-text mb-2 group-hover:text-primary transition-colors">
              {post.title}
            </SubH>
            {post.summary && (
              <p className="text-text-muted text-sm line-clamp-2">
                {post.summary}
              </p>
            )}
            {showDate && post.publishAt && (
              <time className="block text-sm text-text-muted mt-2">
                {formatDate(post.publishAt)}
              </time>
            )}
            {showAuthor && post.author && (
              <p className="text-sm text-text-muted mt-2">
                {post.author}
              </p>
            )}
            {readMoreLabel && (
              <span className="inline-block text-sm font-medium text-primary mt-3 group-hover:underline">
                {readMoreLabel}
              </span>
            )}
          </div>
        </a>
      ))}
    </div>
  );
}

/* ---------- Layout: Featured (hero post + list) ---------- */

function FeaturedLayout({
  posts,
  blogBasePath,
  featuredCount,
  listCount,
  SubH,
}: {
  posts: BlogPost[];
  blogBasePath: string;
  featuredCount: number;
  listCount: number;
  SubH: "h2" | "h3" | "h4";
}) {
  const featured = posts.slice(0, featuredCount);
  const rest = posts.slice(featuredCount, featuredCount + listCount);

  return (
    <div className="space-y-8">
      {/* Featured posts */}
      {featured.map((post) => (
        <a
          key={post.id}
          href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
          className="block bg-white text-text rounded-lg shadow-md overflow-hidden group"
        >
          <div className="grid grid-cols-1 @md:grid-cols-2">
            {post.featuredImageUrl && (
              <div className="aspect-video @md:aspect-auto overflow-hidden">
                <img
                  src={post.featuredImageUrl}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  loading="lazy"
                />
              </div>
            )}
            <div className="p-6 @md:p-8 flex flex-col justify-center">
              <SubH className="text-2xl @md:text-3xl font-bold text-text mb-3 group-hover:text-primary transition-colors">
                {post.title}
              </SubH>
              {post.summary && (
                <p className="text-text-muted leading-relaxed mb-3">
                  {post.summary}
                </p>
              )}
              {post.publishAt && (
                <time className="text-sm text-text-muted">
                  {formatDate(post.publishAt)}
                </time>
              )}
            </div>
          </div>
        </a>
      ))}

      {/* Rest as a compact list */}
      {rest.length > 0 && (
        <div className="space-y-4">
          {rest.map((post) => (
            <a
              key={post.id}
              href={`${blogBasePath}/${formatDateSlug(post.publishAt, post.slug)}`}
              className="flex items-baseline gap-4 py-3 border-b border-current/10 last:border-b-0 group"
            >
              <SubH className="font-semibold text-text group-hover:text-primary transition-colors flex-1">
                {post.title}
              </SubH>
              {post.publishAt && (
                <time className="text-sm text-text-muted shrink-0">
                  {formatDate(post.publishAt)}
                </time>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Main component ---------- */

export function Component(props: Props) {
  const result = schema.safeParse(props);
  const safe = result.success ? result.data : defaults;
  const ctx = useRenderContext();
  const blogBasePath = ctx.blogBasePath || "/blog";
  const SubH = subHeadingLevel(safe.headingLevel);

  const totalNeeded =
    safe.layout === "featured"
      ? safe.featuredCount + safe.listCount
      : safe.count;

  // When pinnedPostIds is set, render pinned posts first (in order),
  // then fill remaining slots from the existing tag-filtered/recent flow
  // (excluding already-pinned ones).
  const rawPosts: BlogPostEntry[] = ctx.blogPosts || [];

  // Apply the section's configured tagFilter client-side so multiple
  // blog sections on the same page can filter independently.
  const allPosts: BlogPost[] = safe.tagFilter
    ? rawPosts.filter((p) => p.tags?.some((t) => t.slug === safe.tagFilter))
    : rawPosts;
  let posts: BlogPost[];
  if (safe.pinnedPostIds && safe.pinnedPostIds.length > 0) {
    const pinnedIds = safe.pinnedPostIds;
    const pinned = pinnedIds
      .map((id) => allPosts.find((p) => p.id === id))
      .filter((p): p is BlogPost => !!p);
    const pinnedIdSet = new Set(pinnedIds);
    const remaining = allPosts.filter((p) => !pinnedIdSet.has(p.id));
    posts = [...pinned, ...remaining].slice(0, totalNeeded);
  } else {
    posts = allPosts.slice(0, totalNeeded);
  }

  if (posts.length === 0) {
    return null;
  }

  return (
    <SectionShell globals={safe}>
        <div className="text-center mb-12">
          <RichText
            as={safe.headingLevel}
            html={safe.heading}
            className="text-3xl @md:text-[2.5rem] font-bold mb-4"
            data-prop="heading"
          />
          {safe.subheading && (
            <RichText
              as="div"
              html={safe.subheading}
              className="text-lg opacity-80 max-w-2xl mx-auto"
              data-prop="subheading"
            />
          )}
        </div>
        {safe.layout === "featured" ? (
          <FeaturedLayout
            posts={posts}
            blogBasePath={blogBasePath}
            featuredCount={safe.featuredCount}
            listCount={safe.listCount}
            SubH={SubH}
          />
        ) : (
          <GridLayout posts={posts} blogBasePath={blogBasePath} SubH={SubH} columns={safe.columns} showDate={safe.showDate} showAuthor={safe.showAuthor} readMoreLabel={safe.readMoreLabel} imageAspect={safe.imageAspect} />
        )}
    </SectionShell>
  );
}
